import { Alert, Box, Paper, Skeleton, Stack, Typography } from '@mui/material';
import { useState } from 'react';

import { AnalisisFormPlaceholderDialog } from '@/features/analisis/components/AnalisisFormPlaceholderDialog';
import { BaermannFormDialog } from '@/features/analisis/baermann/components/BaermannFormDialog';
import { CoprocultivoFormDialog } from '@/features/analisis/coprocultivo/components/CoprocultivoFormDialog';
import { EctoparasitosFormDialog } from '@/features/analisis/ectoparasitos/components/EctoparasitosFormDialog';
import { SedimentacionFhFormDialog } from '@/features/analisis/sedimentacion-fh/components/SedimentacionFhFormDialog';
import { useBaermannListByProtocolo } from '@/features/analisis/baermann/hooks/useBaermann';
import { useCoprocultivoListByProtocolo } from '@/features/analisis/coprocultivo/hooks/useCoprocultivo';
import { useEctoparasitosListByProtocolo } from '@/features/analisis/ectoparasitos/hooks/useEctoparasitos';
import { HpgFormDialog } from '@/features/analisis/hpg/components/HpgFormDialog';
import { useHpgListByProtocolo } from '@/features/analisis/hpg/hooks/useHpg';
import { useOpgListByProtocolo } from '@/features/analisis/opg/hooks/useOpg';
import { useSedimentacionFhListByProtocolo } from '@/features/analisis/sedimentacion-fh/hooks/useSedimentacionFh';
import { useEstablecimiento } from '@/features/establecimientos/hooks/useEstablecimientos';
import { TipoAnalisis } from '@/types/enums';

import { agruparAnalisis, type GrupoAnalisis } from '../grupoAnalisis';

import type { TipoCarga } from './AnalisisToolbar';
import { AnalisisToolbar } from './AnalisisToolbar';
import { EliminarAnalisisDialog } from './EliminarAnalisisDialog';
import { GrupoAnalisisRow } from './GrupoAnalisisRow';

type DialogState = {
  tipo: TipoCarga;
  modo: 'crear' | 'editar';
  uidAnalisis?: string;
} | null;

interface SeccionAnalisisProps {
  uidProtocolo: string;
  canManage: boolean;
  isClosed: boolean;
  numeroProtocolo?: number;
  uidEstablecimiento?: string;
}

function LoadingSkeleton(): JSX.Element {
  return (
    <Stack spacing={1}>
      {[0, 1, 2].map((i) => (
        <Skeleton key={i} variant="rounded" height={64} />
      ))}
    </Stack>
  );
}

export function SeccionAnalisis({
  uidProtocolo,
  canManage,
  isClosed,
  numeroProtocolo,
  uidEstablecimiento,
}: SeccionAnalisisProps): JSX.Element {
  const [deleteTarget, setDeleteTarget] = useState<GrupoAnalisis | null>(null);
  const [dialog, setDialog] = useState<DialogState>(null);

  const establecimientoQuery = useEstablecimiento(uidEstablecimiento);

  const hpgQuery = useHpgListByProtocolo(uidProtocolo);
  const opgQuery = useOpgListByProtocolo(uidProtocolo);
  const coprocultivoQuery = useCoprocultivoListByProtocolo(uidProtocolo);
  const baermannQuery = useBaermannListByProtocolo(uidProtocolo);
  const sedFhQuery = useSedimentacionFhListByProtocolo(uidProtocolo);
  const ectoQuery = useEctoparasitosListByProtocolo(uidProtocolo);

  const queries = [hpgQuery, opgQuery, coprocultivoQuery, baermannQuery, sedFhQuery, ectoQuery];
  const isLoading = queries.some((q) => q.isLoading);
  const hasError = queries.some((q) => q.isError);

  const grupos = agruparAnalisis(
    hpgQuery.data ?? [],
    opgQuery.data ?? [],
    coprocultivoQuery.data ?? [],
    baermannQuery.data ?? [],
    sedFhQuery.data ?? [],
    ectoQuery.data ?? [],
  );

  const showActions = canManage && !isClosed;

  function handleCargar(tipo: TipoCarga): void {
    setDialog({ tipo, modo: 'crear' });
  }

  function handleEditar(grupo: GrupoAnalisis): void {
    if (grupo.tipoGrupo === 'hpg') {
      setDialog({ tipo: 'hpg', modo: 'editar', uidAnalisis: grupo.hpg.uid_analisis });
      return;
    }

    switch (grupo.tipo) {
      case TipoAnalisis.COPROCULTIVO:
        setDialog({
          tipo: 'coprocultivo',
          modo: 'editar',
          uidAnalisis: grupo.analisis.uid_analisis,
        });
        break;
      case TipoAnalisis.BAERMANN:
        setDialog({ tipo: 'baermann', modo: 'editar', uidAnalisis: grupo.analisis.uid_analisis });
        break;
      case TipoAnalisis.SEDIMENTACION_FH:
        setDialog({
          tipo: 'sedimentacion_fh',
          modo: 'editar',
          uidAnalisis: grupo.analisis.uid_analisis,
        });
        break;
      case TipoAnalisis.ECTOPARASITOS:
        setDialog({
          tipo: 'ectoparasitos',
          modo: 'editar',
          uidAnalisis: grupo.analisis.uid_analisis,
        });
        break;
    }
  }

  function closeDialog(): void {
    setDialog(null);
  }

  return (
    <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
      <Box mb={2.5}>
        <Typography variant="h6">Análisis</Typography>
        <Typography variant="body2" color="text.secondary">
          Cargá un nuevo análisis o editá los existentes.
        </Typography>
      </Box>

      {showActions && <AnalisisToolbar onCargar={handleCargar} />}

      {hasError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          No se pudieron cargar todos los análisis. Recargá la página.
        </Alert>
      )}

      {isLoading ? (
        <LoadingSkeleton />
      ) : grupos.length === 0 ? (
        <Box sx={{ py: 4, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Aún no se cargaron análisis.
          </Typography>
        </Box>
      ) : (
        <Stack spacing={1}>
          {grupos.map((grupo) => (
            <GrupoAnalisisRow
              key={grupo.tipoGrupo === 'hpg' ? grupo.hpg.uid_analisis : grupo.analisis.uid_analisis}
              grupo={grupo}
              numeroProtocolo={numeroProtocolo}
              canManage={showActions}
              onEliminar={() => setDeleteTarget(grupo)}
              onEditar={handleEditar}
            />
          ))}
        </Stack>
      )}

      <EliminarAnalisisDialog
        open={deleteTarget !== null}
        uidProtocolo={uidProtocolo}
        grupo={deleteTarget}
        onCancel={() => setDeleteTarget(null)}
        onSuccess={() => setDeleteTarget(null)}
      />

      {dialog?.tipo === 'hpg' && (
        <HpgFormDialog
          open
          onClose={closeDialog}
          uidProtocolo={uidProtocolo}
          uidAnalisis={dialog.uidAnalisis}
          numeroProtocolo={numeroProtocolo}
          establecimientoNombre={establecimientoQuery.data?.nombre}
        />
      )}

      {dialog?.tipo === 'coprocultivo' && (
        <CoprocultivoFormDialog
          open
          onClose={closeDialog}
          uidProtocolo={uidProtocolo}
          uidAnalisis={dialog.uidAnalisis}
          numeroProtocolo={numeroProtocolo}
          establecimientoNombre={establecimientoQuery.data?.nombre}
        />
      )}

      {dialog?.tipo === 'baermann' && (
        <BaermannFormDialog
          open
          onClose={closeDialog}
          uidProtocolo={uidProtocolo}
          uidAnalisis={dialog.uidAnalisis}
          numeroProtocolo={numeroProtocolo}
          establecimientoNombre={establecimientoQuery.data?.nombre}
        />
      )}

      {dialog?.tipo === 'ectoparasitos' && (
        <EctoparasitosFormDialog
          open
          onClose={closeDialog}
          uidProtocolo={uidProtocolo}
          uidAnalisis={dialog.uidAnalisis}
          numeroProtocolo={numeroProtocolo}
          establecimientoNombre={establecimientoQuery.data?.nombre}
        />
      )}

      {dialog?.tipo === 'sedimentacion_fh' && (
        <SedimentacionFhFormDialog
          open
          onClose={closeDialog}
          uidProtocolo={uidProtocolo}
          uidAnalisis={dialog.uidAnalisis}
          numeroProtocolo={numeroProtocolo}
          establecimientoNombre={establecimientoQuery.data?.nombre}
        />
      )}

      {dialog !== null &&
        dialog.tipo !== 'hpg' &&
        dialog.tipo !== 'coprocultivo' &&
        dialog.tipo !== 'baermann' &&
        dialog.tipo !== 'ectoparasitos' &&
        dialog.tipo !== 'sedimentacion_fh' && (
          <AnalisisFormPlaceholderDialog open onClose={closeDialog} tipo={dialog.tipo} />
        )}
    </Paper>
  );
}
